import React, {useEffect, useReducer, useState} from 'react'

import {useRoute, RouteType, Route, TenantEditRoute, useRouteDispatch} from './route'

import {Login} from './login'
import {Base} from './base'

import {UserList} from './routes/userList'
import {UserRoleList} from './routes/userRoleList'

import {AppContext, appContextInitialState, appContextReducer} from './control/appContext'
import {TenantList} from './routes/tenantList'
import {TenantEditPanel} from './panel/tenantEditPanel'

import {CopyData} from './routes/copyData'
import {TenantGroups} from './routes/tenantGroups'

import {postData} from './utility'
import {RouteActionType} from './router'

function contentForRoute(route: Route) {
  switch (route.type) {
    case RouteType.Login:
      return <Login />

    case RouteType.UserList:
    case RouteType.UserCreate:
    case RouteType.UserEdit:
      return <UserList />

    case RouteType.UserRoleList:
    case RouteType.UserRoleCreate:
    case RouteType.UserRoleEdit:
      return <UserRoleList />

    case RouteType.TenantList:
      return <TenantList />

    case RouteType.CopyData:
      return <CopyData />

    case RouteType.TenantGroups:
      return <TenantGroups />

    case RouteType.NotFound:
      return null

    default:
      return <TenantList />
  }
}

export function App() {
  const appContext = useReducer(appContextReducer, appContextInitialState)
  const dispatch = useRouteDispatch()

  const [response, setResponse] = useState<{status: number; message: string} | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const {current} = useRoute()
  if (!current) {
    return null
  }

  let comp = null
  switch (current.type) {
    case RouteType.Login:
      comp = <Login />
      break

    case RouteType.TenantCreate:
    case RouteType.TenantEdit:
      comp = (
        <TenantEditPanel
          onSave={async (tenant, oldData) => {
            try {
              setResponse(null)
              setIsLoading(true)

              const res = await postData(
                '/kubectl',
                JSON.stringify({
                  tenant,
                  oldTenant: oldData?.tenant,
                })
              )

              const jobId = res.data?.jobId
              if (!jobId) throw new Error('No jobId returned from server')

              let status = 'pending'
              let errorMessage = ''

              while (status === 'pending') {
                const pollRes = await fetch(`/jobStatus/${jobId}`)
                const statusData = await pollRes.json()

                status = statusData.status
                if (status === 'error') {
                  errorMessage = statusData.error
                  break
                }

                await new Promise((resolve) => setTimeout(resolve, 3000)) // Poll every 3s
              }

              setIsLoading(false)

              if (status === 'success') {
                setResponse({status: 200, message: '✅ Deployment complete'})
                dispatch({
                  type: RouteActionType.PushRoute,
                  route: TenantEditRoute.create({id: tenant.id}),
                })
              } else {
                setResponse({status: 500, message: `❌ Deployment failed: ${errorMessage}`})
              }
            } catch (err: any) {
              setIsLoading(false)
              setResponse({status: 500, message: `❌ Error: ${err.message || 'Unexpected error'}`})
              console.error(err)
            }
          }}
          id={current.type === RouteType.TenantEdit ? current.params.id : undefined}
          response={response}
          isApplyLoading={isLoading}
        />
      )
      break
    default:
      comp = <Base>{contentForRoute(current)}</Base>
  }

  return <AppContext.Provider value={appContext}>{comp}</AppContext.Provider>
}
